import { isMobilePlatform } from '@renderer/lib/runtime'

export type WebcamConfig = {
  deviceId: string
  label: string
  resolution?: {
    width: number
    height: number
  }
}

function createDrawSurface(
  width: number,
  height: number
): {
  draw: (video: HTMLVideoElement, dw: number, dh: number) => void
  toJpegBlob: (quality: number) => Promise<Blob>
} {
  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(width, height)
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Failed to get canvas context')
    return {
      draw: (video, dw, dh): void => {
        ctx.drawImage(video, 0, 0, dw, dh)
      },
      toJpegBlob: (quality): Promise<Blob> =>
        canvas.convertToBlob({
          type: 'image/jpeg',
          quality
        })
    }
  }

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Failed to get canvas context')
  return {
    draw: (video, dw, dh): void => {
      ctx.drawImage(video, 0, 0, dw, dh)
    },
    toJpegBlob: (quality): Promise<Blob> =>
      new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error('canvas.toBlob failed'))),
          'image/jpeg',
          quality
        )
      })
  }
}

export class Webcam {
  private videoElement: HTMLVideoElement | null = null
  private webcamStream: MediaStream | null = null

  static async getDevices(): Promise<MediaDeviceInfo[]> {
    const devices = await navigator.mediaDevices.enumerateDevices()
    return devices.filter((device) => device.kind === 'videoinput')
  }

  private ensureVideoElement(): HTMLVideoElement {
    if (!this.videoElement) {
      const v = document.createElement('video')
      v.setAttribute('playsinline', '')
      v.setAttribute('webkit-playsinline', 'true')
      v.setAttribute('aria-hidden', 'true')
      v.muted = true
      v.playsInline = true
      v.autoplay = true
      Object.assign(v.style, {
        position: 'fixed',
        left: '0',
        top: '0',
        width: '1px',
        height: '1px',
        opacity: '0',
        pointerEvents: 'none'
      })
      document.body.appendChild(v)
      this.videoElement = v
    }
    return this.videoElement
  }

  private async waitForVideoDimensions(video: HTMLVideoElement, timeoutMs = 10000): Promise<void> {
    if (video.videoWidth > 0 && video.videoHeight > 0) return

    await new Promise<void>((resolve, reject) => {
      const deadline = window.setTimeout(() => {
        video.removeEventListener('loadedmetadata', onReady)
        video.removeEventListener('loadeddata', onReady)
        video.removeEventListener('canplay', onReady)
        reject(new Error('Webcam video dimensions timeout'))
      }, timeoutMs)

      const onReady = (): void => {
        if (video.videoWidth > 0 && video.videoHeight > 0) {
          window.clearTimeout(deadline)
          video.removeEventListener('loadedmetadata', onReady)
          video.removeEventListener('loadeddata', onReady)
          video.removeEventListener('canplay', onReady)
          resolve()
        }
      }

      video.addEventListener('loadedmetadata', onReady)
      video.addEventListener('loadeddata', onReady)
      video.addEventListener('canplay', onReady)
      onReady()
    })
  }

  async connect(deviceId?: string): Promise<void> {
    const video = this.ensureVideoElement()
    const mobile = isMobilePlatform()
    const idealW = mobile ? 1920 : 3840
    const idealH = mobile ? 1080 : 2160

    const constraints = {
      video:
        deviceId && deviceId !== ''
          ? {
              deviceId: { exact: deviceId },
              frameRate: { ideal: 30, max: 60 },
              width: { ideal: idealW },
              height: { ideal: idealH }
            }
          : {
              frameRate: { ideal: 30, max: 60 },
              width: { ideal: idealW },
              height: { ideal: idealH }
            }
    }
    this.webcamStream = await navigator.mediaDevices.getUserMedia(constraints)
    video.srcObject = this.webcamStream

    try {
      await video.play()
    } catch (firstError) {
      console.warn('[Webcam] play() failed, retrying', firstError)
      await video.play()
    }

    await this.waitForVideoDimensions(video)
  }

  async disconnect(): Promise<void> {
    if (this.webcamStream) {
      const tracks = this.webcamStream.getTracks()
      tracks.forEach((track) => {
        track.enabled = false
        track.stop()
      })
      this.webcamStream = null

      if (this.videoElement) {
        this.videoElement.srcObject = null
        this.videoElement.remove()
        this.videoElement = null
      }
    }
  }

  async getConfig(): Promise<WebcamConfig> {
    if (this.webcamStream && this.videoElement) {
      const tracks = this.webcamStream.getVideoTracks()
      const track = tracks[0]
      return {
        deviceId: track.getSettings().deviceId || '',
        label: track.label,
        resolution: {
          width: this.videoElement.videoWidth,
          height: this.videoElement.videoHeight
        }
      }
    }
    throw new Error('No webcam stream available')
  }

  async capturePreviewAsImageBitmap(): Promise<ImageBitmap | undefined> {
    if (!this.videoElement) return undefined

    const w = this.videoElement.videoWidth
    const h = this.videoElement.videoHeight
    if (w === 0 || h === 0) return undefined

    if (typeof OffscreenCanvas !== 'undefined') {
      const canvas = new OffscreenCanvas(w, h)
      const ctx = canvas.getContext('2d')
      if (!ctx) return undefined
      ctx.drawImage(this.videoElement, 0, 0, canvas.width, canvas.height)
      return createImageBitmap(canvas)
    }

    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) return undefined
    ctx.drawImage(this.videoElement, 0, 0, canvas.width, canvas.height)
    return createImageBitmap(canvas)
  }

  async capturePreviewAsBlob(
    maxWidth?: number,
    maxHeight?: number,
    quality?: number
  ): Promise<Blob> {
    if (!this.videoElement) throw new Error('Webcam is not connected')

    const originalWidth = this.videoElement.videoWidth
    const originalHeight = this.videoElement.videoHeight
    if (originalWidth === 0 || originalHeight === 0) {
      throw new Error('Webcam frame not ready')
    }

    let targetWidth = originalWidth
    let targetHeight = originalHeight

    if (maxWidth && maxHeight) {
      const aspectRatio = originalWidth / originalHeight
      if (originalWidth > maxWidth || originalHeight > maxHeight) {
        if (aspectRatio > maxWidth / maxHeight) {
          targetWidth = maxWidth
          targetHeight = maxWidth / aspectRatio
        } else {
          targetHeight = maxHeight
          targetWidth = maxHeight * aspectRatio
        }
      }
    }

    targetWidth = Math.max(1, Math.round(targetWidth))
    targetHeight = Math.max(1, Math.round(targetHeight))

    const surface = createDrawSurface(targetWidth, targetHeight)
    surface.draw(this.videoElement, targetWidth, targetHeight)
    return surface.toJpegBlob(quality ?? 1.0)
  }

  async captureImageAsFile(maxWidth?: number, maxHeight?: number, quality?: number): Promise<File> {
    const blob = await this.capturePreviewAsBlob(maxWidth, maxHeight, quality)
    return new File([blob], 'captured_image.jpg', { type: 'image/jpeg' })
  }
}
